/**
 * Tasks Editor - Editable task list for story tasks
 * Story 227: Edit PRDs, tech designs and tasks
 * Story 292: Delete tasks from web UI with coverage warning
 *
 * Features:
 * - Drag-and-drop reordering
 * - Expandable task rows showing full details
 * - Inline title editing
 * - Rich text body editor (same as section editor)
 * - Task type selector dropdown
 * - Anchor checklist linking tasks to design capabilities
 * - Delete button for proposed tasks (with coverage warning)
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTaskMutations } from '../../hooks/useTaskMutations';
import { SplitPaneEditor } from '../editor/SplitPaneEditor';
import { InsertZone } from '../editor/InsertZone';
import { CoverageDropdown } from '../editor/CoverageDropdown';
import { ScalesIcon } from '../icons/ScalesIcon';
import { TrashIcon } from '../icons/TrashIcon';
import { TaskEvidenceModal } from './TaskEvidenceModal';
import { DeleteTaskWarningModal } from './DeleteTaskWarningModal';
import type { TaskItem } from '../../hooks/useStoryTasks';
import './TasksEditor.css';

/** Story 292: Affected capability for delete warning */
interface AffectedCapability {
  id: string;
  title: string;
}

/** Story 292: Undo toast duration in milliseconds */
const UNDO_TOAST_DURATION_MS = 5000;

/** Debounce delay for body autosave in milliseconds */
const BODY_AUTOSAVE_DELAY_MS = 1000;

/** Task types available for selection - must match VALID_TASK_TYPES in evaluation.ts */
const TASK_TYPES = [
  { value: 'implementation', label: 'Implementation' },
  { value: 'bugfix', label: 'Bug Fix' },
  { value: 'test:unit', label: 'Unit Test' },
  { value: 'test:ui-component', label: 'UI Component Test' },
  { value: 'test:integration', label: 'Integration Test' },
  { value: 'test:e2e', label: 'E2E Test' },
] as const;

/** Status options for dropdown (Story 302) */
const STATUS_OPTIONS = [
  { value: 'proposed', label: 'Proposed' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
] as const;

interface TasksEditorProps {
  projectSlug: string;
  storyId: string;
  tasks: TaskItem[];
  /** Available design anchors for the implements checklist */
  designAnchors: string[];
  /** Map of design anchor -> section title for display in coverage dropdown */
  designTitleMap?: Record<string, string>;
  /** Called when user clicks an anchor to navigate to design section */
  onNavigateToDesign?: (anchor: string) => void;
}

interface ExpandedTaskState {
  seq: number;
  title: string;
  body: string;
  taskType: string;
  implements: string[];
}

export function TasksEditor({
  projectSlug,
  storyId,
  tasks: initialTasks,
  designAnchors,
  designTitleMap = {},
  onNavigateToDesign,
}: TasksEditorProps) {
  const [tasks, setTasks] = useState<TaskItem[]>(initialTasks);
  const [expandedTask, setExpandedTask] = useState<number | null>(null);
  const [editingState, setEditingState] = useState<ExpandedTaskState | null>(null);
  const bodySaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Story 289: Evidence modal state
  const [evidenceModalTask, setEvidenceModalTask] = useState<TaskItem | null>(null);
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);

  // Story 292: Delete task state
  const [undoToast, setUndoToast] = useState<{ seq: number; title: string } | null>(null);
  const undoTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Story 292: Warning modal state
  const [deleteWarning, setDeleteWarning] = useState<{
    task: TaskItem;
    affectedCapabilities: AffectedCapability[];
  } | null>(null);

  const { updateTask, createTask, reorderTasks, deleteTask, restoreTask } = useTaskMutations(projectSlug, storyId);

  // Compute which design anchors are not covered by ANY task
  const uncoveredAnchors = useMemo(() => {
    const coveredByAnyTask = new Set<string>();
    for (const task of tasks) {
      if (task.implements) {
        for (const anchor of task.implements) {
          coveredByAnyTask.add(anchor);
        }
      }
    }
    return new Set(designAnchors.filter((anchor) => !coveredByAnyTask.has(anchor)));
  }, [tasks, designAnchors]);

  // Compute which anchors are covered by other tasks (excluding specific task)
  const getCoveredByOtherTasks = useCallback((excludeSeq: number) => {
    const covered = new Set<string>();
    for (const task of tasks) {
      if (task.seq !== excludeSeq && task.implements) {
        for (const anchor of task.implements) {
          covered.add(anchor);
        }
      }
    }
    return covered;
  }, [tasks]);

  // Sync local state with props
  useEffect(() => {
    setTasks(initialTasks);
  }, [initialTasks]);

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - reorder tasks
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeSeq = Number(active.id);
    const overSeq = Number(over.id);

    const activeIndex = tasks.findIndex((t) => t.seq === activeSeq);
    const overIndex = tasks.findIndex((t) => t.seq === overSeq);

    if (activeIndex === -1 || overIndex === -1) return;

    // Reorder locally
    const newTasks = [...tasks];
    const [removed] = newTasks.splice(activeIndex, 1);
    newTasks.splice(overIndex, 0, removed);
    setTasks(newTasks);

    // Send new order to server
    const newOrder = newTasks.map((t) => t.seq);
    reorderTasks.mutate(newOrder);
  }, [tasks, reorderTasks]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (bodySaveTimeoutRef.current) {
        clearTimeout(bodySaveTimeoutRef.current);
      }
    };
  }, []);

  // Save and collapse task
  const handleCollapse = useCallback(() => {
    if (editingState) {
      // Clear any pending debounced save
      if (bodySaveTimeoutRef.current) {
        clearTimeout(bodySaveTimeoutRef.current);
        bodySaveTimeoutRef.current = null;
      }
      // Save final state
      updateTask.mutate({
        seq: editingState.seq,
        updates: {
          title: editingState.title,
          body: editingState.body,
          task_type: editingState.taskType,
          implements: editingState.implements,
        },
      });
    }
    setExpandedTask(null);
    setEditingState(null);
  }, [editingState, updateTask]);

  const handleExpand = useCallback((task: TaskItem) => {
    if (expandedTask === task.seq) {
      // Collapse with save
      handleCollapse();
    } else {
      // Expand and load editing state
      setExpandedTask(task.seq);
      setEditingState({
        seq: task.seq,
        title: task.title,
        body: task.body ?? '',
        taskType: task.task_type ?? 'implementation',
        implements: task.implements ?? [],
      });
    }
  }, [expandedTask, handleCollapse]);

  const handleTitleChange = useCallback((newTitle: string) => {
    if (!editingState) return;
    setEditingState((prev) => prev ? { ...prev, title: newTitle } : null);
  }, [editingState]);

  const handleBodyChange = useCallback((newBody: string) => {
    if (!editingState) return;
    setEditingState((prev) => prev ? { ...prev, body: newBody } : null);

    // Debounced autosave for body
    if (bodySaveTimeoutRef.current) {
      clearTimeout(bodySaveTimeoutRef.current);
    }
    bodySaveTimeoutRef.current = setTimeout(() => {
      updateTask.mutate({
        seq: editingState.seq,
        updates: { body: newBody },
      });
    }, BODY_AUTOSAVE_DELAY_MS);
  }, [editingState, updateTask]);

  const handleTaskTypeChange = useCallback((newType: string) => {
    if (!editingState) return;
    setEditingState((prev) => prev ? { ...prev, taskType: newType } : null);
  }, [editingState]);

  // Implements changes save immediately (dropdown is always accessible)
  const handleImplementsChange = useCallback((seq: number, newImplements: string[]) => {
    // Update local tasks state for immediate UI feedback
    setTasks((prev) => prev.map((t) =>
      t.seq === seq ? { ...t, implements: newImplements } : t
    ));
    // Update editingState if this task is expanded
    if (editingState && editingState.seq === seq) {
      setEditingState((prev) => prev ? { ...prev, implements: newImplements } : null);
    }
    // Save immediately
    updateTask.mutate({
      seq,
      updates: { implements: newImplements },
    });
  }, [editingState, updateTask]);

  // Story 302: Status changes save immediately
  const handleStatusChange = useCallback((seq: number, newStatus: TaskItem['status']) => {
    // Update local tasks state for immediate UI feedback
    setTasks((prev) => prev.map((t) =>
      t.seq === seq ? { ...t, status: newStatus } : t
    ));
    // Save immediately
    updateTask.mutate({
      seq,
      updates: { status: newStatus },
    });
  }, [updateTask]);

  const handleSave = useCallback(() => {
    if (!editingState) return;
    updateTask.mutate({
      seq: editingState.seq,
      updates: {
        title: editingState.title,
        body: editingState.body,
        task_type: editingState.taskType,
        implements: editingState.implements,
      },
    });
  }, [editingState, updateTask]);

  const handleAddTask = useCallback(() => {
    createTask.mutate({
      title: 'New Task',
      task_type: 'implementation',
    });
  }, [createTask]);

  // Story 289: Evidence modal handlers
  const handleEvidenceClick = useCallback((task: TaskItem) => {
    setEvidenceModalTask(task);
    setEvidenceModalOpen(true);
  }, []);

  const handleCloseEvidenceModal = useCallback(() => {
    setEvidenceModalOpen(false);
    setEvidenceModalTask(null);
  }, []);

  /**
   * Story 292: Execute the actual delete and show undo toast
   */
  const executeDelete = useCallback((task: TaskItem) => {
    deleteTask.mutate(task.seq, {
      onSuccess: () => {
        // Show undo toast
        setUndoToast({ seq: task.seq, title: task.title });

        // Clear any existing timeout
        if (undoTimeoutRef.current) {
          clearTimeout(undoTimeoutRef.current);
        }

        // Auto-dismiss after 5 seconds
        undoTimeoutRef.current = setTimeout(() => {
          setUndoToast(null);
        }, UNDO_TOAST_DURATION_MS);
      },
    });
  }, [deleteTask]);

  /**
   * Story 292: Get capabilities that would become uncovered if task is deleted
   */
  const getAffectedCapabilities = useCallback((task: TaskItem): AffectedCapability[] => {
    if (!task.implements || task.implements.length === 0) {
      return [];
    }

    const coveredByOthers = getCoveredByOtherTasks(task.seq);
    const affected: AffectedCapability[] = [];

    for (const capabilityId of task.implements) {
      // If this capability is not covered by any other task, it would become uncovered
      if (!coveredByOthers.has(capabilityId)) {
        affected.push({
          id: capabilityId,
          title: designTitleMap[capabilityId] || capabilityId,
        });
      }
    }

    return affected;
  }, [getCoveredByOtherTasks, designTitleMap]);

  /**
   * Story 292: Handle delete task click
   * Checks coverage impact and shows warning if needed
   */
  const handleDeleteTask = useCallback((task: TaskItem) => {
    const affected = getAffectedCapabilities(task);

    if (affected.length > 0) {
      // Show warning modal
      setDeleteWarning({ task, affectedCapabilities: affected });
    } else {
      // No coverage impact, delete directly
      executeDelete(task);
    }
  }, [getAffectedCapabilities, executeDelete]);

  /**
   * Story 292: Handle "Delete Anyway" from warning modal
   */
  const handleDeleteAnyway = useCallback(() => {
    if (deleteWarning) {
      executeDelete(deleteWarning.task);
      setDeleteWarning(null);
    }
  }, [deleteWarning, executeDelete]);

  /**
   * Story 292: Handle cancel from warning modal
   */
  const handleCancelDelete = useCallback(() => {
    setDeleteWarning(null);
  }, []);

  /**
   * Story 292: Handle navigation to capability from warning modal
   */
  const handleNavigateFromWarning = useCallback((capabilityId: string) => {
    setDeleteWarning(null);
    onNavigateToDesign?.(capabilityId);
  }, [onNavigateToDesign]);

  /**
   * Story 292: Handle undo delete
   */
  const handleUndoDelete = useCallback(() => {
    if (!undoToast) return;

    // Clear the timeout
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }

    // Restore the task
    restoreTask.mutate(undoToast.seq);

    // Hide the toast
    setUndoToast(null);
  }, [undoToast, restoreTask]);

  /**
   * Story 292: Dismiss undo toast
   */
  const handleDismissUndoToast = useCallback(() => {
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }
    setUndoToast(null);
  }, []);

  // Cleanup undo timeout on unmount
  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
    };
  }, []);

  // Task row height + border for InsertZone gap calculation
  const TASK_ROW_GAP = 8;

  return (
    <div className="tasks-editor" data-testid="tasks-editor">
      <div className="tasks-editor__header">
        <span className="tasks-editor__header-drag"></span>
        <span className="tasks-editor__header-expand"></span>
        <span className="tasks-editor__header-id">ID</span>
        <span className="tasks-editor__header-title">Title</span>
        <span className="tasks-editor__header-type">Type</span>
        <span className="tasks-editor__header-implements">Implements</span>
        <span className="tasks-editor__header-status">Status</span>
        <span className="tasks-editor__header-evidence"></span>
        <span className="tasks-editor__header-delete"></span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tasks.map((t) => t.seq)}
          strategy={verticalListSortingStrategy}
        >
          {/* Insert zone at the beginning */}
          <InsertZone onInsert={handleAddTask} label="Add task" gap={TASK_ROW_GAP} />

          {tasks.map((task, index) => (
            <div key={task.seq}>
              <SortableTaskRow
                task={task}
                storyId={storyId}
                isExpanded={expandedTask === task.seq}
                editingState={expandedTask === task.seq ? editingState : null}
                designAnchors={designAnchors}
                designTitleMap={designTitleMap}
                uncoveredAnchors={uncoveredAnchors}
                getCoveredByOtherTasks={getCoveredByOtherTasks}
                onExpand={() => handleExpand(task)}
                onTitleChange={handleTitleChange}
                onBodyChange={handleBodyChange}
                onTaskTypeChange={handleTaskTypeChange}
                onImplementsChange={handleImplementsChange}
                onSave={handleSave}
                onCollapse={handleCollapse}
                onNavigateToDesign={onNavigateToDesign}
                onEvidenceClick={handleEvidenceClick}
                onDelete={handleDeleteTask}
                onStatusChange={handleStatusChange}
              />
              {/* Insert zone after each task */}
              {index < tasks.length - 1 && (
                <InsertZone onInsert={handleAddTask} label="Add task" gap={TASK_ROW_GAP} />
              )}
            </div>
          ))}

          {/* Insert zone at the end */}
          {tasks.length > 0 && (
            <InsertZone onInsert={handleAddTask} label="Add task" gap={TASK_ROW_GAP} />
          )}
        </SortableContext>
      </DndContext>

      {/* Story 289: Evidence modal */}
      {evidenceModalTask && (
        <TaskEvidenceModal
          isOpen={evidenceModalOpen}
          onClose={handleCloseEvidenceModal}
          storyExternalId={storyId}
          task={evidenceModalTask}
        />
      )}

      {/* Story 292: Delete warning modal */}
      {deleteWarning && (
        <DeleteTaskWarningModal
          isOpen={true}
          affectedCapabilities={deleteWarning.affectedCapabilities}
          onCancel={handleCancelDelete}
          onDeleteAnyway={handleDeleteAnyway}
          onNavigateToCapability={handleNavigateFromWarning}
        />
      )}

      {/* Story 292: Undo toast */}
      {undoToast && (
        <div className="tasks-editor__undo-toast" role="alert">
          <span>Task deleted</span>
          <button
            className="tasks-editor__undo-btn"
            onClick={handleUndoDelete}
          >
            Undo
          </button>
          <button
            className="tasks-editor__undo-dismiss"
            onClick={handleDismissUndoToast}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

/** Props for SortableTaskRow */
interface SortableTaskRowProps {
  task: TaskItem;
  storyId: string;
  isExpanded: boolean;
  editingState: ExpandedTaskState | null;
  designAnchors: string[];
  /** Map of design anchor -> section title for display */
  designTitleMap: Record<string, string>;
  /** Anchors not covered by ANY task (for red highlighting) */
  uncoveredAnchors: Set<string>;
  /** Get anchors covered by other tasks (excluding this task) */
  getCoveredByOtherTasks: (excludeSeq: number) => Set<string>;
  onExpand: () => void;
  onTitleChange: (title: string) => void;
  onBodyChange: (body: string) => void;
  onTaskTypeChange: (type: string) => void;
  onImplementsChange: (seq: number, anchors: string[]) => void;
  onSave: () => void;
  onCollapse: () => void;
  /** Called when user clicks an anchor to navigate to design section */
  onNavigateToDesign?: (anchor: string) => void;
  /** Story 289: Called when user clicks evidence icon */
  onEvidenceClick: (task: TaskItem) => void;
  /** Story 292: Called when user clicks delete button */
  onDelete: (task: TaskItem) => void;
  /** Story 302: Called when user changes task status */
  onStatusChange: (seq: number, status: TaskItem['status']) => void;
}

/** Sortable task row with drag handle */
function SortableTaskRow({
  task,
  storyId,
  isExpanded,
  editingState,
  designAnchors,
  designTitleMap,
  uncoveredAnchors,
  getCoveredByOtherTasks,
  onExpand,
  onTitleChange,
  onBodyChange,
  onTaskTypeChange,
  onImplementsChange,
  onSave,
  onCollapse,
  onNavigateToDesign,
  onEvidenceClick,
  onDelete,
  onStatusChange,
}: SortableTaskRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.seq });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="tasks-editor__task"
      {...attributes}
    >
      <div
        className={`tasks-editor__row ${isExpanded ? 'tasks-editor__row--expanded' : ''}`}
        data-testid={`task-row-${task.seq}`}
      >
        {/* Drag handle */}
        <button
          className="tasks-editor__drag-handle"
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
            <path d="M6 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm7-8a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
          </svg>
        </button>

        {/* Story 233: Expand icon positioned left of title to match PRD/Design pattern */}
        <span className="tasks-editor__expand-icon" onClick={onExpand}>
          {isExpanded ? '▼' : '▶'}
        </span>
        <span className="tasks-editor__id" onClick={onExpand}>{storyId}.{task.seq}</span>
        {isExpanded && editingState ? (
          <input
            type="text"
            className="tasks-editor__title-input"
            value={editingState.title}
            onChange={(e) => onTitleChange(e.target.value)}
            onBlur={onSave}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="tasks-editor__title" onClick={onExpand}>{task.title}</span>
        )}
        {/* Task type dropdown in row - RHS flowing with title */}
        <select
          className="tasks-editor__type-select"
          value={editingState?.taskType ?? task.task_type ?? 'implementation'}
          onChange={(e) => {
            e.stopPropagation();
            onTaskTypeChange(e.target.value);
          }}
          onClick={(e) => e.stopPropagation()}
          onBlur={onSave}
        >
          {TASK_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
        {/* Coverage dropdown in row header */}
        <div className="tasks-editor__implements" onClick={(e) => e.stopPropagation()}>
          {designAnchors.length > 0 ? (
            <CoverageDropdown
              allAnchors={designAnchors}
              selected={editingState?.implements ?? task.implements ?? []}
              uncovered={uncoveredAnchors}
              uncoveredTooltip="Not implemented by any task"
              onChange={(anchors) => onImplementsChange(task.seq, anchors)}
              onNavigate={onNavigateToDesign}
              label="Coverage"
              showStatusBadge={true}
              coveredByOthers={getCoveredByOtherTasks(task.seq)}
              coveredByOthersTooltip="Already implemented by another task"
              anchorTitleMap={designTitleMap}
            />
          ) : (
            <span className="tasks-editor__no-anchors">—</span>
          )}
        </div>
        {/* Story 302: Status dropdown */}
        <select
          className={`tasks-editor__status-select tasks-editor__status-select--${task.status}`}
          value={task.status}
          onChange={(e) => {
            e.stopPropagation();
            onStatusChange(task.seq, e.target.value as TaskItem['status']);
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`task-status-${task.seq}`}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {/* Story 289: Evidence column - always render cell for grid alignment */}
        <div className="tasks-editor__evidence-cell">
          {task.status === 'done' && task.has_evidence && (
            <button
              className="tasks-editor__evidence-btn"
              onClick={(e) => {
                e.stopPropagation();
                onEvidenceClick(task);
              }}
              aria-label="View completion evidence"
              title="View completion evidence"
            >
              <ScalesIcon size={16} />
            </button>
          )}
        </div>
        {/* Story 292: Delete column - always render cell for grid alignment */}
        <div className="tasks-editor__delete-cell">
          {task.status === 'proposed' && (
            <button
              className="tasks-editor__delete-btn"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(task);
              }}
              aria-label="Delete task"
              title="Delete task"
            >
              <TrashIcon size={16} />
            </button>
          )}
        </div>
      </div>

      {isExpanded && editingState && (
        <div className="tasks-editor__details">
          <div className="tasks-editor__field">
            <label className="tasks-editor__label">Planning Notes</label>
            <div className="tasks-editor__body-editor">
              <SplitPaneEditor
                initialContent={editingState.body}
                onChange={onBodyChange}
                onEscape={onCollapse}
              />
            </div>
          </div>

          {task.assignee && (
            <div className="tasks-editor__field">
              <label className="tasks-editor__label">Assignee</label>
              <span className="tasks-editor__assignee">@{task.assignee}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
